// =============================================================================
// CLIENT ERROR REPORTER — sends client failures to POST /errors with detail,
// and pushes them to a visible sink (the Toast provider registers one). Uses a
// RAW fetch, never apiFetch, so reporting an apiFetch failure can't recurse.
// Fire-and-forget, throttled, never throws.
// =============================================================================

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api';

export interface ClientError {
  source?: 'client' | 'server';
  severity?: 'error' | 'warn' | 'fatal';
  message: string;
  stack?: string;
  context?: { page?: string; action?: string; status?: number; [k: string]: unknown };
}

// A visible sink (e.g. a toast). The Toast provider registers one; until then,
// errors still persist server-side.
type ErrorSink = (e: ClientError) => void;
let sink: ErrorSink | null = null;
export function setErrorSink(fn: ErrorSink | null): void {
  sink = fn;
}

// Throttle identical message+page within a short window so a render loop can't
// spam the surface or the DB.
const recent = new Map<string, number>();
const THROTTLE_MS = 5000;

export function reportError(e: ClientError): void {
  const page = e.context?.page ?? (typeof location !== 'undefined' ? location.pathname : '');
  const key = `${e.message}|${page}`;
  const now = Date.now();
  const last = recent.get(key) ?? 0;
  if (now - last < THROTTLE_MS) return;
  recent.set(key, now);

  try {
    sink?.(e);
  } catch {
    /* a broken sink must not break reporting */
  }

  void fetch(`${API_BASE}/errors`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      source: e.source ?? 'client',
      severity: e.severity ?? 'error',
      message: e.message,
      stack: e.stack,
      context: { page, ...e.context },
    }),
  }).catch(() => {
    /* fire-and-forget; logging must never throw */
  });
}

// Lightweight breadcrumb for the Studio voice loop. Unlike reportError it is NOT
// throttled and does NOT hit the visible sink (no toast spam): it just pipes a
// lifecycle event to the backend (POST /errors) so the phone/iPad side, whose
// console we cannot see, surfaces in `wrangler tail` + error_logs. severity is
// 'warn' (the backend enum has no 'info'); the message is prefixed so it is easy
// to grep ("studio-trace:"). Fire-and-forget, never throws.
//
// CAPTURE HYGIENE: every trace carries context.kind='trace'. The admin Errors
// dashboard excludes kind='trace' by default so these breadcrumbs (hundreds of
// rows) don't drown the handful of real errors. The tracer is intact — it still
// streams to `wrangler tail` and persists — it just no longer pollutes the
// default error view (there is a toggle to show traces). Do NOT drop the kind:
// the dashboard's default filter depends on it.
export function traceStudio(event: string, context?: Record<string, unknown>): void {
  void fetch(`${API_BASE}/errors`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      source: 'client',
      severity: 'warn',
      message: `studio-trace: ${event}`,
      context: {
        kind: 'trace',
        action: 'studio-voice',
        page: typeof location !== 'undefined' ? location.pathname : '',
        ...context,
      },
    }),
  }).catch(() => {
    /* fire-and-forget */
  });
}
